import React from "react";
import {
  InventoryCertificationOUT,
  ProductHealthCheckedStatusEnum,
} from "api-client";
import { IconCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import SearchSortTable from "components/Table";
import { ColumnDef } from "@tanstack/react-table";
import useDatasetViewTableColumns, {
  EPDGroupColumns,
} from "./useDatasetViewTableColumns";
import { useComplianceInfo } from "hooks/useComplianceInfo";
import { observer } from "mobx-react-lite";
import { searchStore } from "features/ProductSearch/SearchStore";
import ProductChips from "features/BuildingProductEditor/components/ProductChips";

export interface EPDGroup {
  epds: EPDGroupColumns[];
  certifications?: InventoryCertificationOUT[];
  conformity?: string | null;
  selectedGroupID?: boolean;
  health_checked?: ProductHealthCheckedStatusEnum | undefined | null;
}

function DatasetView({
  epdGroup,
  referenceUnitID,
}: {
  epdGroup: EPDGroup | undefined;
  referenceUnitID?: string;
}) {
  const { t } = useTranslation();
  const { getCurrentConformity, getCurrentCertification } = useComplianceInfo();

  const highlightUsedEPDGroup = () =>
    epdGroup?.selectedGroupID && !searchStore.openProductSearch.state;

  function complianceContent(group: EPDGroup | undefined) {
    return (
      <>
        {highlightUsedEPDGroup() ? (
          <div className="bg-green-50 text-green-800 p-2 flex items-center border-b border-gray-200">
            <IconCheck
              width={16}
              className="mr-4 ml-2 p-1 min-w-6 min-h-6 bg-green-100 rounded-full text-green-600"
              stroke={2.5}
            />
            <span className="text-xs font-medium">
              {t("mapping.usedForCalculationAlert", {
                conformity: getCurrentConformity(),
                certification: getCurrentCertification()?.name,
              })}
            </span>
          </div>
        ) : null}
        <div className="pl-3 pt-3">
          <ProductChips
            conformity={group?.conformity ? [group?.conformity] : undefined}
            certifications={group?.certifications?.map((item) => item.name)}
            healthChecked={group?.health_checked}
            containerClassName="mb-3 !mt-0"
          />
        </div>
      </>
    );
  }

  function getColumns() {
    return useDatasetViewTableColumns(referenceUnitID);
  }

  return (
    <div className="border border-gray-300 rounded-md mb-3 last-of-type:mb-0 overflow-hidden">
      {complianceContent(epdGroup)}
      {epdGroup?.epds?.length ? (
        <SearchSortTable
          data={epdGroup.epds}
          getColumns={getColumns as () => ColumnDef<unknown, unknown>[]}
          headerRowStyle="bg-white whitespace-nowrap"
          headerColumnStyle="border-r last-of-type:border-r-0"
          headerDivStyle="!py-1"
          customColumnStyle="border-r last-of-type:border-r-0 whitespace-nowrap !py-1 !px-2"
          scrollDivClassName="bg-white scrollbar"
          enableColumnResizing
          enableTooltip
        />
      ) : null}
    </div>
  );
}

export default observer(DatasetView);
